/**
 * Export builders (pure): CSV per RFC 4180 with '#' metadata header lines,
 * ISO 8601 timestamps, '.' decimal separator, UTF-8.
 */
import type { AreaStats } from './area';
import type { ProfileMetrics } from './metrics';
import type { Profile } from './sampling';
import { num } from './units';

export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'number' ? num(v) : v;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows: (string | number | null | undefined)[][], header: string[] = []): string {
  const lines = header.map((h) => `# ${h.replace(/[\r\n]+/g, ' ')}`);
  for (const r of rows) lines.push(r.map(csvCell).join(','));
  return lines.join('\r\n') + '\r\n';
}

export interface ExportContext {
  app: string;
  generated: string;
  source: string;
  quantity: string;
  unit: string;
  spatialUnit: string;
  /** Size of one pixel in spatialUnit. */
  perPixel: number;
  pipeline: string[];
}

export function contextHeader(c: ExportContext): string[] {
  return [
    `${c.app}`,
    `generated: ${c.generated}`,
    `source: ${c.source}`,
    `quantity: ${c.quantity}${c.unit ? ` [${c.unit}]` : ''}`,
    `spatial scale: 1 px = ${num(c.perPixel)} ${c.spatialUnit}`,
    ...c.pipeline,
  ];
}

export interface ProfileExport {
  name: string;
  geometry: string;
  sampling: string;
  profile: Profile;
  /** Additional aligned columns (e.g. reference image B, derivative). */
  extra?: { name: string; values: ArrayLike<number> }[];
}

export function profileCsv(ctx: ExportContext, p: ProfileExport): string {
  const u = ctx.unit ? `_${ctx.unit.replace(/\s+/g, '')}` : '';
  const su = ctx.spatialUnit;
  const cols = [
    'index',
    `distance_${su}`,
    'distance_px',
    'x_px',
    'y_px',
    `value${u}`,
    `band_sd${u}`,
    `band_min${u}`,
    `band_max${u}`,
    'band_count',
    'clipped',
    ...(p.extra ?? []).map((e) => e.name),
  ];
  const pr = p.profile;
  const rows: (string | number)[][] = [cols];
  for (let i = 0; i < pr.n; i++) {
    rows.push([
      i,
      pr.t[i] * ctx.perPixel,
      pr.t[i],
      pr.px[i],
      pr.py[i],
      pr.v[i],
      pr.sd[i],
      pr.lo[i],
      pr.hi[i],
      pr.cnt[i],
      pr.clip[i] & 1 ? 1 : 0,
      ...(p.extra ?? []).map((e) => e.values[i]),
    ]);
  }
  return toCsv(rows, [...contextHeader(ctx), `line: ${p.name}; ${p.geometry}`, `sampling: ${p.sampling}`]);
}

export type MetricRow = [label: string, value: number | string | null, unit: string];

/** Human-readable metric table; distances are converted with `perPixel` (profile t is in px). */
export function metricRows(m: ProfileMetrics, unit: string, spatialUnit: string, perPixel: number): MetricRow[] {
  const d = (v: number | null) => (v === null || !Number.isFinite(v) ? null : v * perPixel);
  const rows: MetricRow[] = [
    ['Samples (valid / total)', `${m.nValid} / ${m.n}`, ''],
    ['Maximum', m.max, unit],
    ['Position of maximum', d(m.xMax), spatialUnit],
    ['Minimum', m.min, unit],
    ['Position of minimum', d(m.xMin), spatialUnit],
    ['Mean', m.mean, unit],
    ['Median', m.median, unit],
    ['Standard deviation', m.sd, unit],
    ['RMS', m.rms, unit],
    ['Coefficient of variation', m.cv, ''],
    ['Michelson contrast', m.michelson, ''],
    ['Uniformity U0 = min/mean', m.u0, ''],
    ['Uniformity U1 = min/max', m.u1, ''],
    ['Non-uniformity (max−min)/mean', m.nonUniformity, ''],
    ['Integral ∫ value d(distance)', m.integral * perPixel, `${unit}·${spatialUnit}`],
    ['Centroid', d(m.centroid), spatialUnit],
    [`Baseline (${m.baseline.mode})`, m.baseline.slope ? `${m.baseline.a} + ${m.baseline.slope / perPixel}·x` : m.baseline.a, unit],
    ['Clipped samples', m.clippedSamples, ''],
  ];
  if (m.edge) {
    const e = m.edge;
    rows.push(
      [`Edge (${e.direction}) low level`, e.low, unit],
      [`Edge high level`, e.high, unit],
      ['Edge 50 % position', d(e.x50), spatialUnit],
      ['Edge transition distance', d(e.distance), spatialUnit],
    );
  }
  return rows;
}

export function peakRows(m: ProfileMetrics, spatialUnit: string, perPixel: number): (string | number | null)[][] {
  const head = ['#', `position [${spatialUnit}]`, 'value', 'height', 'prominence'];
  const levels = m.peaks[0]?.widths.map((w) => w.level) ?? [];
  for (const l of levels) head.push(`FW at ${fmtLevel(l)} [${spatialUnit}]`);
  const rows: (string | number | null)[][] = [head];
  m.peaks.forEach((p, i) =>
    rows.push([
      i + 1,
      p.x * perPixel,
      p.value,
      p.height,
      p.prominence,
      ...p.widths.map((w) => (w.width === null ? null : w.width * perPixel)),
    ]),
  );
  return rows;
}

export function fmtLevel(l: number): string {
  if (Math.abs(l - 0.5) < 1e-9) return '50 % (FWHM)';
  if (Math.abs(l - Math.exp(-2)) < 1e-6) return '1/e² (13.5 %)';
  return `${Number((l * 100).toPrecision(4))} %`;
}

export function areaRows(a: AreaStats, unit: string, spatialUnit: string, perPixel: number): MetricRow[] {
  const rows: MetricRow[] = [
    ['Pixels', a.n, ''],
    ['Area', a.areaPx * perPixel * perPixel, `${spatialUnit}²`],
    ['Mean', a.mean, unit],
    ['Standard deviation', a.sd, unit],
    ['Minimum', a.min, unit],
    ['Maximum', a.max, unit],
    ['Median', a.median, unit],
    ['5th percentile', a.p05, unit],
    ['95th percentile', a.p95, unit],
    ['Sum', a.sum, unit],
    ['Coefficient of variation', a.cv, ''],
    ['Uniformity U0 = min/mean', a.u0, ''],
    ['Uniformity U1 = min/max', a.u1, ''],
    ['Michelson contrast', a.michelson, ''],
    ['Clipped pixels', a.clipped, ''],
  ];
  if (a.color) {
    const c = a.color;
    rows.push(
      ['CIE x', c.x, ''],
      ['CIE y', c.y, ''],
      ["CIE u'", c.up, ''],
      ["CIE v'", c.vp, ''],
      ['CCT', c.cctValid ? c.cct : null, 'K'],
      ['Duv', c.cctValid ? c.duv : null, ''],
      ['Mean X (relative)', c.X, ''],
      ['Mean Y (relative)', c.Y, ''],
      ['Mean Z (relative)', c.Z, ''],
    );
  }
  return rows;
}
